import React, { useState, useEffect, useContext, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { store } from 'components/Store';
import { useLocation, Link, useNavigate } from 'react-router';
import getUrlParam, { decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';
import getCurrentTeam from 'components/Settings/Team/getCurrentTeam';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import PopUpCheckout, { PricingPopUpData } from './PopUpCheckout';
import CouponBanner from './CouponBanner';
import Navigation from 'components/Dashboard/Navigation/index';
import Footer from 'components/home/Footer';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import SkeletonPricing from './SkeletonPricing';
import {
  consultantAgenciesMembers,
  membershipMembers,
} from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import useScrollBody from 'components/home/useScrollBody';
import { loadKeyWords } from 'components/Dashboard/utilities/loadKeyWords';
import getActiveSubscriptions from 'components/Settings/Billing/getActiveSubscriptions';
import useUpdatePaidSubscriptions from 'components/Settings/Billing/useUpdatePaidSubscriptions';
import getActivePlan from 'components/Settings/Utilities/getActivePlan';
import AgencyLoggedOut from './AgencyLoggedOut';
import useGetAccessToken from 'components/Dashboard/apiCalls/useGetAccessToken';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import dayjs from 'dayjs';
import MobileTopToggle from './MobileTopToggle';
import useDashboardUrlCondition from 'components/Dashboard/Navigation/useDashboardUrlCondition';
import getAppSite from 'utils/getAppSite';
import PricingPageMatrix from 'components/Pricing/PricingPageMatrix';
import {
  FreePlan,
  BasicPlan,
  ProPlan,
  MogulPlan,
  CustomPlan,
  DataHubPlan,
  PublicAffairsSuitePlan,
} from 'components/Pricing/PlanCards';
import MCPInformationBox from 'components/Pricing/MCPInformationBox';

const PricingPage = () => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { readyKeywordLists, keywordsLists, team, activeSubscriptions } = state;
  const [selectedPlanInformation, setSelectedPlanInformation] = useState({
    period: 'annually',
    periodPopup: 'annually',
  });
  const { myself } = team ?? {};
  const { period, periodPopup } = selectedPlanInformation;
  const [planInformation, setPlanInformation] = useState({});
  const [activeTab, setActiveTab] = useState('companies');

  const { planName: mainPlanName, isLegacyPlan, isPaperInvoicing, subscriptionStatus } = activeSubscriptions;
  const planName = mainPlanName === 'Campaigner' ? 'Starter' : mainPlanName;
  const { trialEndDateUtc } = planInformation;
  const [isLoading, setIsLoading] = useState(true);
  const highlightedChannel = import.meta.env.VITE_APP_HIGHLIGHTED_CHANNEL;
  const [planToCheckOut, setPlanToCheckout] = useState(highlightedChannel);
  const [showPopUpCheckout, setShowPopUpCheckout] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const showFreePlan = import.meta.env.VITE_APP_SHOW_FREE_PLAN.toLowerCase() !== 'false';
  const { isAuthenticated } = useAuth0();
  const { getAccessToken } = useGetAccessToken();
  const { addMetaTags } = useAddMetaTags();
  //AE: These conditions are different from the billing page although at first glance it looks almost the same
  const bannerConditions =
    (isPaperInvoicing ||
      planName === 'Enterprise' ||
      subscriptionStatus === 'OnFreePlan' ||
      subscriptionStatus === 'OnFreePlanOverLimit' ||
      isLegacyPlan ||
      (myself?.role !== 'Owner' && isAuthenticated)) &&
    getAppSite() !== 'usa';

  const showTweets = import.meta.env.VITE_APP_ENABLE_TWITTER === 'true';
  const membershipUser = membershipMembers(activeSubscriptions?.planCode);
  const { bannerText, freePlan } = useUpdatePaidSubscriptions();
  const conditionForUpgrade = isLegacyPlan || freePlan || membershipUser;

  const initialLoad = useRef(null);
  initialLoad.current = (source) => {
    try {
      if (isAuthenticated) {
        loadActivePlanInformation(source);
      } else {
        let title = `Pricing - PolicyMogul`;
        let hash = location.hash;
        addMetaTags({ title, hash });
        setIsLoading(false);
      }
    } catch (error) {}
  };

  useScrollBody();
  useEffect(() => {
    const source = new AbortController();
    initialLoad.current(source);
    return () => {
      source.abort('Pricing page canceled by the user.');
    };
  }, []);

  const loadActivePlanInformation = async (source) => {
    try {
      let activePlan = await getActivePlan(getAccessToken, source);
      if (!!activePlan) {
        const { code, allowMultipleTeams } = activePlan;
        if (consultantAgenciesMembers(code) && allowMultipleTeams) {
          return navigate('/');
        }
      }
      let activeSubscriptions = await getActiveSubscriptions(getAccessToken, source);
      if (!readyKeywordLists) {
        let keywordsLists = await loadKeyWords(getAccessToken, source);
        dispatch({ type: 'MODIFY_SECTION', parameter: 'keywordsLists', value: keywordsLists });
      }
      let currentTeam = await getCurrentTeam(getAccessToken, source);
      if (activeSubscriptions) {
        dispatch({ type: 'MODIFY_SECTION', parameter: 'activeSubscriptions', value: activeSubscriptions });
      }
      if (currentTeam) {
        dispatch({ type: 'MODIFY_SECTION', parameter: 'team', value: currentTeam });
      }
      let defineInitialActiveTab = () => {
        let code = activePlan?.code;
        if (consultantAgenciesMembers(code)) {
          return 'agencies';
        } else {
          return 'companies';
        }
      };
      setActiveTab(defineInitialActiveTab());

      if (activePlan) {
        setPlanInformation(activePlan);
        dispatch({ type: 'MODIFY_SECTION', parameter: 'activePlan', value: activePlan });
      }
      if (activeSubscriptions) {
        dispatch({ type: 'MODIFY_SECTION', parameter: 'activeSubscriptions', value: activeSubscriptions });
        const billingPeriod = activeSubscriptions.billingPlanName?.toLowerCase().includes('month') ? 'monthly' : 'annually';
        setSelectedPlanInformation((prev) => ({ ...prev, period: billingPeriod, periodPopup: billingPeriod }));
        let title = `Pricing - PolicyMogul`;
        let hash = location.hash;
        addMetaTags({ title, hash });
        setIsLoading(false);
      }
    } catch (error) {
      //createNotification('danger', `An error ocurred.`)
    }
  };

  const goToElement = () => {
    const featuresElement = document.getElementById('features');
    if (!featuresElement) {
      return;
    }

    const top = featuresElement.getBoundingClientRect().top + window.scrollY - 75;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const showPopUp = () => {
    setShowPopUpCheckout(true);
  };

  const paymentButtonProps = {
    activeTab,
    selectedPlanInformation,
    setPlanToCheckout,
    showPopUp,
    bannerConditions,
    conditionForUpgrade,
  };

  const calculateDifference = (date) => {
    const date1 = dayjs();
    const date2 = dayjs(date);
    const difference = -date1.diff(date2, 'day');
    return `${difference} day${difference !== 1 ? 's' : ''}`;
  };

  const ExpiresMessage = () => {
    return (
      <>
        {(subscriptionStatus === 'TrialEnded' ||
          subscriptionStatus === 'SubscriptionEnded' ||
          subscriptionStatus === 'InTrial') && (
          <div
            className='mogul-message'
            style={{
              top: `-${subscriptionStatus === 'TrialEnded' ? 30 : 30}px`, //51
            }}
          >
            {subscriptionStatus === 'InTrial' && (
              <p className='date-text'>expires in {calculateDifference(trialEndDateUtc)}</p>
            )}
            <p className='message-text py-2'>{subscriptionStatus === 'InTrial' ? 'Current ' : 'Expired '} plan</p>
          </div>
        )}
      </>
    );
  };

  const TrialStartsMessage = () => {
    return (
      <>
        {activeTab === 'companies' && (
          <div
            className='mogul-message'
            style={{
              top: `-30`,
            }}
          >
            <p className='message-text py-2'>Trial starts here</p>
          </div>
        )}
      </>
    );
  };

  const isMobile = useIsMobile();
  const { defaultUrl } = useDashboardUrlCondition();
  const param = getUrlParam('returnUrl');
  const toggleOptions = useRef(null);
  const [toggleDimentions, setToggleDimentions] = useState({ left: 0, width: '254px' });
  const changeTopToggleOption = (e, option) => {
    setActiveTab(option);
    if (!isMobile) {
      let { left, width } = e.target.getBoundingClientRect();
      let finalLeft = left - toggleOptions.current.getBoundingClientRect().left;
      setToggleDimentions({ left: finalLeft - 1, width });
    }
  };

  const agencyLoggeout = !isAuthenticated && activeTab === 'agencies';

  return (
    <div className='pricing-page ' id='scroll-container'>
      {isLoading && <SkeletonPricing />}
      {!isLoading && (
        <>
          {!isAuthenticated && <Navigation />}
          {isAuthenticated && (
            <div className='py-3 px-3 px-lg-5 main-navigation-logo settings-header' style={{ position: 'relative' }}>
              <div className='mx-0 justify-content-between align-items-center row '>
                <Link to={`/${defaultUrl}`}>
                  <img src={`${import.meta.env.VITE_APP_CDNURL}/images/logo.svg`} alt='Logo' />
                </Link>
                {subscriptionStatus !== 'TrialEnded' &&
                  subscriptionStatus !== 'OnFreePlanOverLimit' &&
                  subscriptionStatus !== 'SubscribedOverLimit' && (
                    <Link
                      className='close-settings'
                      to={`${param !== undefined ? decodeUrlQueryValue(param) : `/${defaultUrl}`}`}
                    />
                  )}
              </div>
            </div>
          )}

          <div className='pricing-page-heading py-lg-5 pt-5 pb-3'>
            <div className='container text-center'>
              <h1 className='mt-5'>Find the plan that’s right for you</h1>
              {!isAuthenticated && getAppSite() !== 'usa' && (
                <>
                  {isMobile && <MobileTopToggle activeTab={activeTab} changeTopToggleOption={changeTopToggleOption} />}
                  {!isMobile && (
                    <div
                      className='justify-content-center mt-5 pricing-toggle-tabs position-relative'
                      ref={toggleOptions}
                    >
                      <div
                        className='pricing-toggle-indicator'
                        style={{
                          transform: `translateX(${toggleDimentions.left}px)`,
                          width: `${toggleDimentions.width}px`,
                        }}
                      />
                      <button
                        className={`general-button action-state-hover toggle-pricing-button ${
                          activeTab === 'companies' ? 'active' : ''
                        }`}
                        onClick={(e) => {
                          changeTopToggleOption(e, 'companies');
                        }}
                      >
                        For companies and charities
                      </button>
                      <button
                        className={`general-button action-state-hover toggle-pricing-button ${
                          activeTab === 'agencies' ? 'active' : ''
                        }`}
                        onClick={(e) => {
                          changeTopToggleOption(e, 'agencies');
                        }}
                      >
                        For agencies and consultants
                      </button>
                    </div>
                  )}
                </>
              )}

              <CouponBanner />
              {bannerConditions && (
                <p className={`bg-main-blue text-white rounded py-2 px-4 mt-5 pricing-unauthenticated-banner-text`}>
                  {bannerText()}.{' '}
                  {subscriptionStatus !== 'SubscriptionEnded' && myself?.role === 'Owner' && (
                    <>
                      <span
                        className={`contact-button-launch-messenger pointer text-yellow text-underline-hover ${getAppSite()}`}
                      >
                        {' '}
                        Contact us
                      </span>{' '}
                      to amend your subscription.
                    </>
                  )}
                </p>
              )}
              {getAppSite() === 'usa' ? (
                <USCards
                  agencyLoggeout={agencyLoggeout}
                  period={period}
                  selectedPlanInformation={selectedPlanInformation}
                  setSelectedPlanInformation={setSelectedPlanInformation}
                  highlightedChannel={highlightedChannel}
                  activeTab={activeTab}
                  showFreePlan={showFreePlan}
                  paymentButtonProps={paymentButtonProps}
                  goToElement={goToElement}
                  planName={planName}
                  showTweets={showTweets}
                  isAuthenticated={isAuthenticated}
                  ExpiresMessage={ExpiresMessage}
                  TrialStartsMessage={TrialStartsMessage}
                />
              ) : (
                <GlobalCards
                  agencyLoggeout={agencyLoggeout}
                  period={period}
                  selectedPlanInformation={selectedPlanInformation}
                  setSelectedPlanInformation={setSelectedPlanInformation}
                  highlightedChannel={highlightedChannel}
                  activeTab={activeTab}
                  showFreePlan={showFreePlan}
                  paymentButtonProps={paymentButtonProps}
                  goToElement={goToElement}
                  planName={planName}
                  showTweets={showTweets}
                  isAuthenticated={isAuthenticated}
                  ExpiresMessage={ExpiresMessage}
                  TrialStartsMessage={TrialStartsMessage}
                />
              )}
            </div>
          </div>
          {!agencyLoggeout && getAppSite() !== 'usa' && (
            <PricingPageMatrix
              activeTab={activeTab}
              highlightedChannel={highlightedChannel}
              paymentButtonProps={paymentButtonProps}
              planName={planName}
            />
          )}
          {isAuthenticated && (
            <div className='vat-message'>
              <div className='bottom-terms'>
                <div className='row mx-0 pt-2 pb-3 align-items-center'>
                  <a
                    className='text-uppercase'
                    href='https://policymogul.com/'
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    PolicyMogul
                  </a>
                  <span className='dot' />
                  <a
                    href='https://about.policymogul.com/terms-and-conditions/'
                    rel='noopener noreferrer'
                    target='_blank'
                  >
                    T&Cs
                  </a>
                  <div className='px-2'></div>
                  <a href='https://about.policymogul.com/privacy-policy/' rel='noopener noreferrer' target='_blank'>
                    Privacy policy
                  </a>
                </div>
              </div>
            </div>
          )}

          <PopUpCheckout
            targetPlan={planToCheckOut}
            setPlanToCheckout={setPlanToCheckout}
            setSelectedPlanInformation={setSelectedPlanInformation}
            selectedPlanInformation={selectedPlanInformation}
            period={periodPopup}
            activePlanName={planName}
            activeTab={activeTab}
            keywordsLists={keywordsLists}
            showPopUpCheckout={showPopUpCheckout}
            setShowPopUpCheckout={setShowPopUpCheckout}
          />

          <ReactTooltip
            id={'pricing-page-tooltip'}
            effect={'solid'}
            type={'info'}
            className={'pricing-feature-tooltip header-tooltip'}
            backgroundColor={'#00122B'}
            textColor={'#fff'}
            multiline={true}
            arrowColor={'transparent'}
            place={'right'}
            /* globalEventOff={isMobile ? 'click' : undefined}  */
          />
        </>
      )}
      {!isAuthenticated && <Footer />}
    </div>
  );
};

const GlobalCards = ({
  agencyLoggeout,
  period,
  selectedPlanInformation,
  setSelectedPlanInformation,
  highlightedChannel,
  activeTab,
  showFreePlan,
  paymentButtonProps,
  goToElement,
  planName,
  showTweets,
  isAuthenticated,
  ExpiresMessage,
  TrialStartsMessage,
}) => {
  return (
    <>
      {!agencyLoggeout ? (
        <>
          <BillingPeriodToggle
            period={period}
            selectedPlanInformation={selectedPlanInformation}
            setSelectedPlanInformation={setSelectedPlanInformation}
          />

          <MCPInformationBox />

          <div
            className={`row justify-content-center pt-5 pb-lg-4 ${
              highlightedChannel === 'Pro' ? 'px-lg-5' : ''
            } ${activeTab !== 'companies' ? 'agencies-padding-pricing' : ''}`}
          >
            {activeTab === 'companies' && (
              <>
                {showFreePlan && (
                  <div
                    className='col-lg price-box mb-5 mb-lg-0'
                    style={{
                      transform: `rotateY(${period === 'annually' ? '0' : '180'}deg)`,
                    }}
                  >
                    <div className='side-a'>
                      <FreePlan paymentButtonProps={paymentButtonProps} goToElement={goToElement} />
                    </div>
                    <div className='side-b'>
                      <FreePlan paymentButtonProps={paymentButtonProps} goToElement={goToElement} />
                    </div>
                  </div>
                )}

                {!showFreePlan && (
                  <div
                    className='col-lg price-box mb-5 mb-lg-0'
                    style={{
                      transform: `rotateY(${period === 'annually' ? '0' : '180'}deg)`,
                    }}
                  >
                    <div className='side-a'>
                      <BasicPlan
                        period={'annually'}
                        price={PricingPopUpData['Starter'].priceAnnually}
                        paymentButtonProps={paymentButtonProps}
                        goToElement={goToElement}
                      />
                    </div>
                    <div className='side-b'>
                      <BasicPlan
                        period={'monthly'}
                        price={PricingPopUpData['Starter'].priceMonthly}
                        paymentButtonProps={paymentButtonProps}
                        goToElement={goToElement}
                      />
                    </div>
                  </div>
                )}

                <div
                  className={`col-lg price-box mb-5 mb-lg-0 ${
                    highlightedChannel === 'Pro' ? 'highlighted-channel' : ''
                  }`}
                  style={{
                    transform: `rotateY(${period === 'annually' ? '0' : '180'}deg)`,
                  }}
                >
                  <div className='side-a'>
                    {planName === 'Pro' && <ExpiresMessage />}
                    <ProPlan
                      period={'annually'}
                      price={PricingPopUpData['Pro'].priceAnnually}
                      paymentButtonProps={paymentButtonProps}
                      showTweets={showTweets}
                      goToElement={goToElement}
                    />
                  </div>
                  <div className='side-b'>
                    {planName === 'Pro' && <ExpiresMessage />}
                    <ProPlan
                      period={'monthly'}
                      price={PricingPopUpData['Pro'].priceMonthly}
                      paymentButtonProps={paymentButtonProps}
                      showTweets={showTweets}
                      goToElement={goToElement}
                    />
                  </div>
                </div>
              </>
            )}

            <div
              className={`col-lg price-box mb-5 mb-lg-0 ${highlightedChannel === 'Mogul' ? 'highlighted-channel' : ''}`}
              style={{
                transform: `rotateY(${period === 'annually' ? '0' : '180'}deg)`,
              }}
            >
              <div className='side-a position-relative'>
                {!isAuthenticated && <TrialStartsMessage />}
                {planName === 'Mogul' && <ExpiresMessage />}
                <MogulPlan
                  period={'annually'}
                  price={PricingPopUpData['Mogul'].priceAnnually}
                  additionalPrice={PricingPopUpData['Mogul'].categoryPriceAnnually}
                  agencyPrice={PricingPopUpData['Mogul'].agencyPriceAnnually}
                  paymentButtonProps={paymentButtonProps}
                  showTweets={showTweets}
                  goToElement={goToElement}
                />
              </div>
              <div className='side-b'>
                {!isAuthenticated && <TrialStartsMessage />}
                {planName === 'Mogul' && <ExpiresMessage />}
                <MogulPlan
                  period={'monthly'}
                  price={PricingPopUpData['Mogul'].priceMonthly}
                  additionalPrice={PricingPopUpData['Mogul'].categoryPriceMonthly}
                  agencyPrice={PricingPopUpData['Mogul'].agencyPriceMonthly}
                  paymentButtonProps={paymentButtonProps}
                  showTweets={showTweets}
                  goToElement={goToElement}
                />
              </div>
            </div>

            {highlightedChannel === 'Mogul' && (
              <div
                className='col-lg price-box mb-5 mb-lg-0'
                style={{
                  transform: `rotateY(${period === 'annually' ? '0' : '180'}deg)`,
                }}
              >
                <div className='side-a'>
                  <CustomPlan
                    period={'annually'}
                    price={850}
                    paymentButtonProps={paymentButtonProps}
                    planName={planName}
                    showTweets={showTweets}
                    goToElement={goToElement}
                  />
                </div>
                <div className='side-b'>
                  <CustomPlan
                    period={'monthly'}
                    price={850}
                    paymentButtonProps={paymentButtonProps}
                    planName={planName}
                    showTweets={showTweets}
                    goToElement={goToElement}
                  />
                </div>
              </div>
            )}
          </div>
          <p className='my-2'>
            *{' '}
            {activeTab === 'agencies'
              ? 'Minimum of 3 clients. Billed annually as £4,740 per client'
              : `Additional topics can be purchased for £${period === 'annually' ? 95 : 125}/month`}{' '}
          </p>
          <p className='my-2'>** Subject to early access conditions</p>
          <p>
            <strong>Prices are exclusive of VAT</strong>
          </p>
        </>
      ) : (
        <AgencyLoggedOut />
      )}
    </>
  );
};

const BillingPeriodToggle = ({ period, selectedPlanInformation, setSelectedPlanInformation }) => {
  return (
    <div className='d-flex toggle-container align-items-center justify-content-center my-5'>
      <p className={`${period === 'monthly' ? 'active-period' : ''}`}>Monthly</p>
      <button
        className={`general-button mx-4  toggle-component pricing-toggle ${
          period === 'annually' ? 'toggle-annually' : 'toggle-inactive'
        }`}
        onClick={() => {
          if (period === 'annually') {
            setSelectedPlanInformation({
              ...selectedPlanInformation,
              period: 'monthly',
              periodPopup: 'monthly',
            });
          } else {
            setSelectedPlanInformation({
              ...selectedPlanInformation,
              period: 'annually',
              periodPopup: 'annually',
            });
          }
        }}
      />
      <p className={`${period === 'annually' ? 'active-period' : ''} position-relative`}>
        Annual {period !== 'annually' && <span className='annual-period-savings-badge'>(Save 20%)</span>}
      </p>
    </div>
  );
};

const USCards = ({
  highlightedChannel,
  activeTab,
  paymentButtonProps,
  goToElement,
  planName,
  showTweets,
  period,
  selectedPlanInformation,
  setSelectedPlanInformation,
}) => {
  return (
    <>
      <BillingPeriodToggle
        period={period}
        selectedPlanInformation={selectedPlanInformation}
        setSelectedPlanInformation={setSelectedPlanInformation}
      />

      <div
        className={`row justify-content-center pt-5 pb-lg-4 ${
          highlightedChannel === 'Pro' ? 'px-lg-5' : ''
        } ${activeTab !== 'companies' ? 'agencies-padding-pricing' : ''}`}
      >
        <div
          className={`col-lg price-box-usa mb-5 mb-lg-0 ${highlightedChannel === 'DataHub' ? 'highlighted-channel' : ''}`}
          style={{
            transform: `rotateY(${period === 'annually' ? '0' : '180'}deg)`,
          }}
        >
          <div className='side-a position-relative'>
            <DataHubPlan
              period={'annually'}
              price={PricingPopUpData['DataHub'].priceAnnually}
              additionalPrice={PricingPopUpData['DataHub'].categoryPriceAnnually}
              agencyPrice={PricingPopUpData['DataHub'].agencyPriceAnnually}
              paymentButtonProps={paymentButtonProps}
              showTweets={showTweets}
              goToElement={goToElement}
            />
          </div>
          <div className='side-b'>
            <DataHubPlan
              period={'monthly'}
              price={PricingPopUpData['DataHub'].priceMonthly}
              additionalPrice={PricingPopUpData['DataHub'].categoryPriceMonthly}
              agencyPrice={PricingPopUpData['DataHub'].agencyPriceMonthly}
              paymentButtonProps={paymentButtonProps}
              showTweets={showTweets}
              goToElement={goToElement}
            />
          </div>
        </div>

        <div
          className={`col-lg price-box-usa mb-5 mb-lg-0 ${highlightedChannel === 'PublicAffairsSuite' ? 'highlighted-channel' : ''}`}
          style={{
            transform: `rotateY(${period === 'annually' ? '0' : '180'}deg)`,
          }}
        >
          <div className='side-a'>
            <PublicAffairsSuitePlan
              period={'annually'}
              price={850}
              paymentButtonProps={paymentButtonProps}
              planName={planName}
              showTweets={showTweets}
              goToElement={goToElement}
            />
          </div>
          <div className='side-b'>
            <PublicAffairsSuitePlan
              period={'monthly'}
              price={850}
              paymentButtonProps={paymentButtonProps}
              planName={planName}
              showTweets={showTweets}
              goToElement={goToElement}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PricingPage;
